import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, Mail, Flame, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const navigate = useNavigate();

  const handleKeyEvent = (e) => {
    // getModifierState works on key events
    if (typeof e.getModifierState === 'function') {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email: email.trim(), password }
      );
      if (onLogin) onLogin(data);
      navigate('/admin');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let message;
      if (typeof detail === 'string') {
        message = detail;
      } else if (status === 401 || status === 400) {
        message = 'Incorrect email or password. Tip: "0" is a zero, not the letter "O".';
      } else if (!err.response) {
        message = 'Network error — cannot reach the server. Please try again.';
      } else {
        message = 'Login failed. Please try again.';
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-base flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <img
              src="/circle-rd-logo.png"
              alt="RDPT Plus"
              className="w-10 h-10 rounded-full object-contain"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(185,28,28,0.5))' }}
            />
            <span className="font-heading text-2xl font-bold text-white">
              RDPT<span className="text-primary-main">+</span>
            </span>
          </div>
          <h1 className="font-heading text-xl tracking-wide uppercase text-white/80">Admin Panel</h1>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-background-surface border border-white/[0.08] rounded-xl p-8 space-y-6"
          data-testid="admin-login-form"
        >
          {error && (
            <div data-testid="login-error" className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg text-sm flex gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-white/70 text-sm font-body flex items-center gap-2">
              <Mail className="w-4 h-4" /> Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@rdptplus.com"
              className="bg-background-base border-white/10 text-white placeholder:text-white/30 focus:border-primary-main"
              data-testid="login-email-input"
              autoComplete="username"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-white/70 text-sm font-body flex items-center gap-2">
              <Lock className="w-4 h-4" /> Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyEvent}
                onKeyUp={handleKeyEvent}
                placeholder="Enter password"
                className="bg-background-base border-white/10 text-white placeholder:text-white/30 focus:border-primary-main pr-10"
                data-testid="login-password-input"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-white/40 hover:text-white/80 transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                data-testid="login-password-toggle"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {capsLockOn && (
              <div
                data-testid="caps-lock-warning"
                className="flex items-center gap-1.5 text-[11px] text-amber-400/90 font-body"
              >
                <AlertTriangle className="w-3 h-3" /> Caps Lock is ON
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-secondary-main hover:bg-secondary-hover text-white font-heading tracking-wider uppercase"
            data-testid="login-submit-button"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>

          <p className="text-center text-xs text-white/30 font-body">
            <a href="/" className="hover:text-primary-main transition-colors">Back to Landing Page</a>
          </p>
        </form>
      </div>
    </div>
  );
}
